import { LightningElement, wire, api } from "lwc";
import * as locales from "./locales";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { BooleanAttr } from "common/helpers";

export type ContentType = {
  title: string | null;
  description: string | null;
};

export default class SocialSharing extends LightningElement {
  @api appId: string | null = null;
  @api content: ContentType | null = null;
  @api public: BooleanAttr = false;
  @api facebookText: string | null = null;
  @api xText: string | null = null;
  @api linkedinText: string | null = null;
  @api streakWeek: string | null = null;
  @api shareUrl: string | null = null;
  @api serverText: BooleanAttr = false;

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = {};

  private get labelOptions() {
    return {
      facebook_text: {
        badge: this.content?.title,
      },
      x_text: {
        badge: this.content?.title,
      },
      streak_text: {
        week: this.streakWeek,
      },
    };
  }

  private get url() {
    const urlToEncode =
      this.shareUrl?.toString() || window.location.href.toString();
    return window.encodeURIComponent(urlToEncode);
  }

  private get description() {
    return window.encodeURIComponent(this.content?.description || "");
  }

  private get facebookUrl() {
    let shareText;
    if (this.streakWeek) {
      shareText = this.labels.streak_text;
    } else {
      shareText =
        this.serverText && this.facebookText
          ? this.facebookText
          : `${this.labels.facebook_text} ${this.content?.title}`;
    }

    const finalShareText = shareText?.toString() || "";

    return `https://www.facebook.com/dialog/share?app_id=${
      this.appId
    }&display=popup&href=${this.url}&description=${
      this.description
    }&quote=${window.encodeURIComponent(finalShareText)}`;
  }

  private get xUrl() {
    let shareText;
    if (this.streakWeek) {
      shareText = this.labels.streak_text;
    } else {
      shareText =
        this.serverText && this.xText
          ? this.xText
          : `${this.labels.x_text} ${this.content?.title}`;
    }

    const finalShareText = shareText?.toString() || "";

    return `https://x.com/share?url=${
      this.url
    }&text=${window.encodeURIComponent(finalShareText)}`;
  }

  // LinkedIn's API is designed for sharing URLs, not custom text.
  // The share text is pulled from the metadata of the shared URL.
  private get linkedInUrl() {
    return `https://www.linkedin.com/sharing/share-offsite/?url=${this.url}`;
  }

  private onClick(e: MouseEvent) {
    e.preventDefault();

    const targetElement = e.currentTarget as HTMLElement;
    const width = targetElement.getAttribute("data-width");
    const height = targetElement.getAttribute("data-height");
    const props = `width=${width},height=${height}`;
    const windowTarget = targetElement.getAttribute("href") || "";

    window.open(windowTarget, "_blank", props);
  }
}
